/** Pipeline B device hook.
 *
 * The daemon still owns the turn model. This hook owns only the browser edges:
 * capture device PCM, play daemon PCM, ACK played frames, and flush on daemon
 * barge-in. It keeps the PTTState-compatible surface consumed by AppShell.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  connectVoiceDaemon,
  VoiceCall,
  VoiceDaemonEvent,
  VoiceDaemonSocket,
  VoiceDaemonState,
} from '../api/voiceDaemon';
import { startStreamMeter, stopStreamMeter } from '../lib/streamMeter';
// DeviceAudioQueue is still the type of queueRef below; the DeviceAudioFrame import
// went unused when the browser playback queue was retired in the Stage-4 swap.
import { DeviceAudioQueue } from '../lib/deviceAudioQueue';
import { armVoiceSession, disarmVoiceSession } from '../lib/audioSession';
import { isNativeShell } from '../api/nativeCall';

export interface VoiceDaemonMeter {
  level: number;
  bars: number[];
  active: boolean;
  error: string | null;
}

export interface UseVoiceDaemon {
  state: VoiceDaemonState;
  /** THE BOX'S CALL, and the only thing that decides whether one exists. Read
   *  the surface's "am I in a call" from this and never from `state` — `state`
   *  describes the call's quality, including `error`, which is a live call in
   *  trouble and not the absence of one. */
  boxCall: VoiceCall | null;
  /** Non-null when the live call was ALREADY RUNNING when this window reached it
   *  — carrying the box's own start time. Adoption is correct and stays; what
   *  this adds is the surface's ability to SAY it, which is what was missing when
   *  a call ran 95 minutes because it looked like one the owner had just made. */
  adopted: { since: number | null } | null;
  /** The PEER is established and media can flow — NOT merely that the box
   *  accepted the session. Anything asserting "connected" reads this. */
  mediaConnected: boolean;
  agentText: string;
  partialTranscript: string;
  muted: boolean;
  /** Seconds this mute has stood, as the BOX measures it. 0 on the edge and
   *  whenever the microphone is open. */
  mutedStandingS: number;
  activeAgent: { domain: string; agent: string };
  userAmplitude: number;
  meter: VoiceDaemonMeter;
  error: string | null;
  start: (domain: string, agent: string) => Promise<void>;
  /** End the call. The ONLY teardown — from every state, including `connecting`,
   *  where ending IS the cancel. The sphere is a call button: two states, no third. */
  end: () => void;
  setMuted: (m: boolean) => void;
}

const ZERO_BARS = new Array(16).fill(0);

export function useVoiceDaemon(): UseVoiceDaemon {
  const [state, setState] = useState<VoiceDaemonState>('idle');
  // THE ADOPTED CALL, MADE VISIBLE. Null means this window started whatever call
  // it holds (or holds none). Non-null carries the box's own start time so the
  // surface can say HOW LONG it has been running — the fact whose absence let a
  // call sit live for 95 minutes because it looked like every other call.
  const [adopted, setAdopted] = useState<{ since: number | null } | null>(null);

  // ── DOES THE BOX HOLD A CALL? (owner ruling 2026-08-05, Ghost countersigned) ─
  //
  // THE ONE AUTHORITATIVE FACT. Sourced from the box's own call_state and from
  // the startSession reply, and from NOTHING ELSE — never from `state`, never
  // from a transport event, never from an error.
  //
  // Before this, "there is a call" was derived from PIPELINE HEALTH: a four-state
  // allowlist in PhoneHost that happened to agree with the box until the media
  // failed. A stall set state='error', which was in neither `active` nor
  // `liveCall`, so the surface denied a call the box was still holding and tore
  // down the CallKit report with it. That is the rail broken in the direction
  // nobody had enforced: the phone showing no call while the box holds one.
  //
  // `state === 'error'` now describes the call's QUALITY. This decides whether it
  // EXISTS. Cleared ONLY when the box says so — call_state:null, which the box
  // broadcasts on close (voice_b_ws.py:350), or session_ended.
  const [boxCall, setBoxCall] = useState<VoiceCall | null>(null);
  const [agentText, setAgentText] = useState('');
  const [partialTranscript, setPartialTranscript] = useState('');
  const [muted, setMutedState] = useState(false);
  const [mutedStandingS, setMutedStandingS] = useState(0);
  const [activeAgent, setActiveAgent] = useState({ domain: 'HOMEPAGE', agent: 'REXY' });
  const [userAmplitude, setUserAmplitude] = useState(0);
  const [meter, setMeter] = useState<VoiceDaemonMeter>({
    level: 0,
    bars: ZERO_BARS,
    active: false,
    error: null,
  });
  // ── THE BANNER, AND WHICH TURN IT BELONGS TO (2026-08-31) ─────────────────
  // The banner used to be one string with no expiry. A per-utterance incident —
  // an utterance dropped because transcription was behind — set it and it then
  // stood until the next connect, call-state or session start, so a fault from
  // one turn was read minutes later as the live state of the call. On 2026-08-29
  // the owner was reading a banner from a different fault while the actual
  // problem was a muted microphone.
  //
  // So an error now carries the TURN it happened in. A condition — playback
  // blocked, a WebRTC state, an end in flight — carries `turn: null` and is
  // untouched by turn traffic, because a new turn does not make it false. An
  // incident is cleared the moment a NEW turn opens, because by then it is
  // history. `setDaemonError` keeps its old signature so every existing caller
  // means exactly what it meant before: a condition, with no turn.
  const [daemonErrorState, setDaemonErrorState] =
    useState<{ text: string | null; turn: string | null }>({ text: null, turn: null });
  const daemonError = daemonErrorState.text;
  const setDaemonError = useCallback(
    (text: string | null) => setDaemonErrorState({ text, turn: null }), []);
  // ── IS THE MEDIA ACTUALLY UP? ────────────────────────────────────────────
  // Distinct from `state`, and deliberately so. session_ready means the BOX
  // ACCEPTED THE SESSION; this means THE PEER CONNECTION IS ESTABLISHED and
  // media can flow. They are different facts and they arrive separately
  // (voiceDaemon.ts:525 vs the session_ready path), so anything asserting
  // "connected" — CallKit above all — must key off this and not off `state`.
  const [mediaConnected, setMediaConnected] = useState(false);

  const socketRef = useRef<VoiceDaemonSocket | null>(null);
  const queueRef = useRef<DeviceAudioQueue | null>(null);
  const currentTurnRef = useRef<string | null>(null);
  const sessionActiveRef = useRef(false);
  const stateRef = useRef(state);
  stateRef.current = state;
  // The box's call, readable from a callback without rebuilding it. end() has to
  // know whether there is anything left to end, and reading that off `state` is
  // the derivation this whole rail exists to stop.
  const boxCallRef = useRef(boxCall);
  boxCallRef.current = boxCall;

  const zeroMic = useCallback(() => {
    setUserAmplitude(0);
    setMeter((prev) => ({ ...prev, level: 0, bars: ZERO_BARS, active: false }));
  }, []);

  const cleanup = useCallback(async () => {
    sessionActiveRef.current = false;
    currentTurnRef.current = null;
    try { stopStreamMeter(); } catch {}
    if (queueRef.current) {
      await queueRef.current.close().catch(() => {});
      queueRef.current = null;
    }
    if (socketRef.current) {
      // Stop the native level poll before the socket goes: a poll running
      // against a closed engine is battery spent on a number nobody reads.
      try { socketRef.current.setLevelReporting(false); } catch {}
      try { socketRef.current.close(); } catch {}
      socketRef.current = null;
    }
    await disarmVoiceSession().catch(() => {});
    setPartialTranscript('');
    setAgentText('');
    setMutedState(false);
    setDaemonError(null);
    setMediaConnected(false);
    zeroMic();
  }, [zeroMic]);

  const onEvent = useCallback((e: VoiceDaemonEvent) => {
    switch (e.type) {
      case 'session_ready':
        setDaemonError(null);
        // A RECONNECT CANNOT INHERIT A DUCK (Part B). session_ready is the one
        // event that arrives on every attach, so it is where a gain left below
        // unity by a call that is no longer in progress gets cleared. Idempotent:
        // the engine early-returns when the gain is already at unity, so this
        // costs nothing on the overwhelmingly common path.
        socketRef.current?.resumePlayback();
        // THE WINDOW DISPLAYS WHAT THE BOX RETURNS. session_ready carries the call's
        // authoritative pair on every attach, so a window that asked for a different
        // agent is corrected here rather than continuing to show its own request.
        if (e.domain && e.agent) setActiveAgent({ domain: e.domain, agent: e.agent });
        setState('listening');
        break;
      case 'transcript_partial':
        setPartialTranscript(e.text);
        if (stateRef.current === 'listening') setState('user_speaking');
        break;
      case 'transcript_final':
        setPartialTranscript('');
        setState('thinking');
        zeroMic();
        // A NEW TURN RETIRES THE PREVIOUS TURN'S INCIDENT, and only that. The
        // functional form reads the value it is replacing, so this needs no
        // dependency on the banner and cannot race a set from the same batch.
        setDaemonErrorState((prev) => (
          prev.turn && prev.turn !== e.turn_key ? { text: null, turn: null } : prev));
        break;
      case 'agent_speaking_start':
        // THE TURN TRAVELS WITH THE RESUME (Part B). This arrives per SENTENCE,
        // not per turn, so a turn offers many resume opportunities and no single
        // lost event can strand the gain at zero. It also DECLARES the playing
        // turn, which is what lets the engine refuse a stale duck by identity.
        socketRef.current?.resumePlayback(e.turn_key);
        if (e.overlap) return;
        currentTurnRef.current = e.turn_key;
        setAgentText(e.agent_text);
        if (stateRef.current !== 'agent_speaking') setState('agent_speaking');
        break;
      case 'agent_speaking_end':
        if (e.turn_key !== currentTurnRef.current) return;
        currentTurnRef.current = null;
        setAgentText('');
        setState('listening');
        break;
      case 'agent_switched':
        setActiveAgent({ domain: e.domain, agent: e.agent });
        break;
      case 'user_barge_in':
        // THE DROP AT THE BROWSER (owner ruling 2026-07-29, blocker 1). The box has
        // already stopped emitting and discarded its pending marks; this silences the
        // audio already inside browser playout, which is the last place she could talk
        // over the owner. The old queueRef.flush() went inert when the browser queue was
        // retired in the Stage-4 swap — the cut looked wired and did nothing.
        // SCOPED TO THE TURN IT BELONGS TO (Part B). A duck naming a turn that is
        // not the one playing is refused by the engine — a late barge-in for turn
        // N arriving during N+1 cannot silence N+1.
        socketRef.current?.duckPlayback(e.turn_key);
        setAgentText('');
        setState('user_speaking');
        break;
      case 'mute_state':
        // ── THE ONE WRITER OF `muted` (load-bearing, build 19) ──────────────
        // The BOX owns the mute state. This is the ONLY assignment to it: no tap
        // sets it optimistically, on this surface or any other. That property is
        // what makes three inputs — the lock screen, this window's button, and
        // any other window — resolve to one state instead of three opinions, and
        // it is what lets every control render an echo rather than its own guess.
        // A second writer here would recreate the two-owner fault the whole mute
        // round exists to remove. Asserted by test, not by intent.
        setMutedState(e.muted);
        // HOW LONG IT HAS STOOD, restated by the box every heartbeat while it
        // stands. A surface that shows only a changed icon showed nothing the
        // owner noticed for ten minutes on 2026-08-29; a surface that can say
        // "muted for 6:14" is stating a fact he cannot read past.
        setMutedStandingS(typeof e.standing_s === 'number' ? e.standing_s : 0);
        break;
      case 'transport_state':
        // THE PEER ITSELF, not the control channel and not the first track.
        // pc.connectionState === 'connected' (voiceDaemon.ts:525) is the point
        // at which the transport is established and media can flow both ways.
        // ontrack ('webrtc_media') and the data channel ('webrtc_control') can
        // both land earlier, so keying on either would assert media before it is
        // true. It moves BOTH ways: a peer that fails or drops stops asserting
        // connected media, so nothing downstream keeps claiming it.
        if (e.transport === 'webrtc') setMediaConnected(e.state === 'connected');
        if (e.state === 'connected') {
          setDaemonError(null);
          if (stateRef.current === 'connecting' || stateRef.current === 'error') {
            setState('listening');
          }
        } else if (e.state === 'playback_blocked') {
          // SEAM 2. Visible — never silent — but NOT a session error and NOT a state
          // change. The call is alive; only the speaker end of it is not.
          const detail = e.detail ? ` (${e.detail})` : '';
          setDaemonError(`PLAYBACK BLOCKED: this device is not playing her audio${detail}`);
        } else if (e.state === 'playback_restored') {
          setDaemonError(null);
        } else if (e.state === 'awaiting_activation') {
          // THE VISIBLE STALL (owner ruling 2026-08-06). CallKit accepted the
          // call and iOS has not handed the audio session over, so nothing has
          // been captured and nothing negotiated. NO TIMER and no manual
          // override — "a timer there is a reaper wearing a different name". The
          // call sits with no audio, VISIBLY, rather than silently.
          setDaemonError('CALLKIT: waiting for iOS to hand over the audio session');
        } else if (e.state === 'end_undelivered') {
          // ── THE NATIVE END REACHED NOTHING (Ghost round 6) ─────────────────
          // sendUserEnd() had no control channel — it is not created until
          // negotiation begins — so the BOX HAS NOT BEEN TOLD. This was emitted
          // by the engine, mapped by the JS half, and then handled by no branch
          // at all: a failure reported into silence, which is worse than not
          // reporting it, because every layer above looks like it worked.
          //
          // The socket relay is what delivers in this window; this says so while
          // it happens, so an end that is still in flight is never mistaken for
          // one that landed.
          setDaemonError('ENDING: no media channel yet — delivering over the socket');
        } else if (e.state === 'box_ended') {
          setDaemonError('THE BOX ENDED THIS CALL');
        } else if (e.state === 'end_flushed') {
          // LINK THREE, REPORTED. The end was buffered, the socket came back, and
          // socket.io has flushed it — so it has left this device. What remains
          // unknown is only the box's answer, and the banner now says exactly
          // that instead of "reconnecting to tell it", which claimed less than
          // was known and left the owner unable to tell a stalled socket from a
          // silent box.
          setDaemonError('ENDING: your end left the device — waiting for the box');
        } else if (e.state === 'reconnecting' || e.state === 'holding' || e.state === 'disconnected') {
          const detail = e.detail ? ` (${e.detail})` : '';
          setDaemonError(`WEBRTC: ${e.state}${detail}`);
        }
        break;
      case 'call_state':
        // A WINDOW MUST NEVER SHOW "NO CALL" WHILE A CALL IS LIVE. This arrives on
        // every connected window the moment a call starts or ends, so a window that
        // asked once and was told "none" cannot go stale.
        if (e.call) {
          // UNCONDITIONAL. This is the box telling every window what is true, and
          // it is not filtered through what this window currently believes.
          setBoxCall(e.call);
          setActiveAgent({ domain: e.call.domain, agent: e.call.agent });
          // A call this window did not start, arriving while it holds none. Same
          // fact as adoption at open, reached from the broadcast instead.
          if (!socketRef.current?.getCall()) {
            setAdopted((prev) => prev
              ?? { since: typeof e.call!.since === 'number' ? e.call!.since : null });
          }
          // This window is not carrying the call — it holds no peer and no mic — but a
          // call IS live, and the sphere is the box's sphere. It shows in-call, and a
          // tap on it ends the call.
          //
          // `error` JOINS `idle` HERE. The old guard was `=== 'idle'` alone, which
          // meant the box's own broadcast could not reach a surface sitting in
          // error — the state a stall puts it in — so the one message that could
          // have corrected the surface was the one it ignored. The error TEXT is
          // deliberately left standing: the call is live and in trouble, and both
          // halves of that are true at once.
          if (stateRef.current === 'idle' || stateRef.current === 'error') {
            setState('listening');
          }
        } else {
          // GHOST'S CONDITION: the box says there is no call, so nothing here may
          // go on claiming one. Unconditional — the old `!socketRef.current?.getCall()`
          // guard meant a window CARRYING a call ignored the box telling it the
          // call was over, which is the same fault as ignoring it while in error.
          setBoxCall(null);
          setAdopted(null);
          setState('idle');
          // THE BOX HAS ANSWERED, SO THE QUESTION COMES DOWN WITH IT.
          //
          // This branch cleared the call and left the banner standing. The only
          // path that cleared `daemonError` was session_ended -> onClose ->
          // cleanup() — and session_ended is emitted to `self.sid`, which the box
          // nulls on every socket drop (voice_b_ws.py detach()). So in the exact
          // case the banner exists for — an end that had to reconnect to be
          // delivered — the one message that could retire it was the one that
          // could not be sent. "ENDING: the box has not confirmed yet" stayed on
          // screen after the box had confirmed, which is the surface asserting
          // something untrue about the box: the rail, in the direction that was
          // still unenforced.
          setDaemonError(null);
        }
        break;
      case 'session_superseded':
        // Another window has the call. This one stops and says so — it does not try to
        // take it back. The call itself is untouched and continues in the other window.
        void cleanup().then(() => {
          setState('idle');
          setDaemonError('CALL MOVED: another window attached to this call — it is live there');
        });
        break;
      case 'session_ended':
        // The box says this call is over — the one other statement that clears the
        // fact. A supersede deliberately does NOT: the call is still live, just
        // carried by another window, and this surface must keep showing it.
        void cleanup().then(() => { setBoxCall(null); setAdopted(null); setState('idle'); });
        break;
      case 'error': {
        if (e.turn_key && e.turn_key !== currentTurnRef.current) return;
        const code = String(e.code || 'VOICE_ERROR');
        const detail = e.detail ? ` (${e.detail})` : '';
        // THE INCIDENT IS STAMPED WITH ITS TURN. An event that names no turn —
        // UTTERANCE_DROPPED_STT_BUSY is emitted from the intake queue, which has
        // no turn to name — is stamped with the turn that was open when it
        // arrived, which is the turn the owner was in the middle of.
        setDaemonErrorState({
          text: `${code}: ${e.reason || 'voice_error'}${detail}`,
          turn: e.turn_key || currentTurnRef.current || null,
        });
        if (e.code === 'TURN_FAILED') setState('listening');
        else setState('error');
        break;
      }
      default:
        break;
    }
  }, [cleanup, zeroMic]);

  /**
   * Open this window onto the box's call.
   *
   * createIfAbsent=true  — a TAP. Starts a call if there is none.
   * createIfAbsent=false — the window merely OPENING. It asks whether a call exists and
   *                        attaches to it; if there is none it changes nothing and
   *                        returns to idle. A window never creates a call by appearing,
   *                        and — the invariant — opening one never ends a call.
   */
  const open = useCallback(async (
    domain: string, agent: string, createIfAbsent: boolean,
  ) => {
    // SINGLE OWNER AT THE HOOK, ONE MIC. start() used to overwrite socketRef with a
    // second wrapper and leave the first live — holding its own socket, peer and
    // microphone. That orphan is what became the second window in the 2026-07-30
    // fight. Whatever was here is closed before anything new opens.
    const previous = socketRef.current;
    socketRef.current = null;
    if (previous) {
      // Only END a call this window was actually in. A glass socket — one kept open
      // purely to hear the box's call_state — has no call to end, and telling the box
      // otherwise would be a window claiming something untrue.
      if (previous.getCall()) { try { previous.endSession(); } catch {} }
      try { previous.close(); } catch {}
    }
    // ITEM 3 — ASKING IS A READ, AND A READ MUST NOT OCCUPY A STATE.
    // The mount probe used to set 'connecting', and the sphere means END in every
    // non-idle state — so tapping the instant the app opened CANCELLED THE PROBE
    // instead of starting a call, and nothing happened. A window merely asking stays
    // idle, so the sphere still means START; it moves only once the box has said there
    // is a call, at which point END is the honest meaning.
    if (createIfAbsent) setState('connecting');
    setDaemonError(null);
    // NO OPTIMISTIC AGENT. The window used to pre-set the pair it REQUESTED, so a
    // trading window showed Arthur while the box had correctly kept the Rexy call.
    // The pair is set below, from the box's answer, and from session_ready after that.
    sessionActiveRef.current = true;
    try {
      await armVoiceSession();
      // NO BROWSER PLAYBACK QUEUE AND NO BROWSER ACK (Sherman F1 swap, 2026-07-29).
      // The agent's voice arrives as an RTP media track and is played by the audio
      // element in voiceDaemon.ts. The box acks a mark when its samples actually leave;
      // the browser cannot verify that and must not claim it.

      const socket = await connectVoiceDaemon({
        domain,
        agent,
        createIfAbsent,
        onEvent,
        onClose: () => {
          void cleanup().then(() => { setAdopted(null); setState('idle'); });
        },
        // THE MIC NEVER OUTLIVES ITS OWNER (Item 2). The wrapper is owned here the
        // instant it exists — before the socket connects and two awaits before the
        // microphone opens. Previously socketRef was only assigned AFTER this whole
        // await resolved, so a negotiation that hung or threw left a live capture with
        // nothing able to close it.
        onCreated: (s) => { socketRef.current = s; },
        // The engine's level, gated by the SAME rule the browser meter uses, so
        // the two surfaces agree about when a level means anything.
        onLevel: (level) => {
          if (!sessionActiveRef.current) return;
          const s = stateRef.current;
          if (s !== 'user_speaking' && s !== 'listening') {
            setUserAmplitude(0);
            return;
          }
          setUserAmplitude(level);
          setMeter((prev) => ({ ...prev, level, bars: ZERO_BARS, active: true }));
        },
        // The answer has arrived: there IS a call. From here the sphere means end, and
        // the surface should say so rather than waiting out attach + negotiation.
        // ── THIS CALL WAS ALREADY RUNNING (owner ruling 2026-08-05) ─────────
        // Adoption is CORRECT — a call is live on the box, so every window shows
        // it, and that is the mirror working as ruled. What was missing is that
        // the owner could not tell an adopted call apart from one he had just
        // started, so a call could run 95 minutes with nothing reaching it. Not
        // a missing timer: a missing CONTROL. `createIfAbsent === false` is the
        // window merely opening, so a call found on THAT path is by definition
        // one it did not start.
        onCallDiscovered: (call) => {
          if (createIfAbsent) return;
          setState('connecting');
          setAdopted({ since: typeof call.since === 'number' ? call.since : null });
        },
      });
      // A start cancelled while this was in flight must not come back to life. The
      // wrapper's own assertLive() normally raises first; this is the belt for a
      // resolution that beat the cancel.
      if (!sessionActiveRef.current) {
        try { socket.close(); } catch {}
        return;
      }

      // THE BOX'S ANSWER. No call means nothing to show — but the socket is KEPT OPEN
      // as glass. Calls now outlive windows, so a window that asked once and closed its
      // socket would sit showing "no call" for as long as one ran elsewhere. Staying
      // connected is what lets voice:call_state reach it the instant a call starts.
      const call = socket.getCall();
      if (!call) {
        socketRef.current = socket;
        await disarmVoiceSession().catch(() => {});
        // The box answered "no call" directly. Same standing as call_state:null.
        setBoxCall(null);
        setState('idle');
        return;
      }
      socketRef.current = socket;
      // THE BOX'S ANSWER IS THE FACT. Set here as well as from the broadcast so an
      // attaching window knows immediately rather than waiting for the next
      // call_state to arrive.
      setBoxCall(call);
      // RENDER WHAT THE BOX RETURNED, never what this window requested.
      setActiveAgent({ domain: call.domain, agent: call.agent });

      // ONE MICROPHONE, ONE AEC INSTANCE (owner ruling 2026-07-29, blocker 3). The
      // meter attaches to the stream already negotiated onto the peer connection rather
      // than opening a second getUserMedia. It reads only — the RTP track remains the
      // sole path the owner's voice takes to the box.
      const micStream = socket.getMicStream();
      if (micStream) {
        await startStreamMeter(micStream, ({ level, bars }) => {
          if (!sessionActiveRef.current) return;
          const s = stateRef.current;
          if (s !== 'user_speaking' && s !== 'listening') {
            setUserAmplitude(0);
            return;
          }
          setUserAmplitude(level);
          setMeter((prev) => ({
            ...prev,
            level,
            bars: bars.length ? bars : ZERO_BARS,
            active: true,
          }));
        });
      } else {
        // ── NO MediaStream TO READ (stage 3b) ────────────────────────────────
        // libwebrtc owns the microphone natively, so there is nothing here to
        // attach an AnalyserNode to. The level arrives from the engine's own
        // stats instead (media-source.audioLevel — the same field the probe
        // already reads). The one thing this branch must NEVER do is open a
        // capture of its own: one microphone, one AEC instance (owner ruling
        // 2026-07-29, blocker 3), and a second getUserMedia here would be the
        // whole reason the seam was designed to push the level rather than let
        // a consumer fetch it.
        //
        // BARS STAY ZERO, DELIBERATELY (owner ruling 2026-08-06, option 3).
        // Stats give a scalar and nothing else; sixteen bands invented from one
        // number is the surface asserting something it never measured. Nothing
        // on the phone renders bars — verified: meter.bars is read only by
        // AudioIOCard under SettingsPage, which is desktop — so what the phone
        // draws is userAmplitude, which is a level and is real.
        socket.setLevelReporting(true);
      }
      setMeter((prev) => ({ ...prev, active: true, error: null }));
    } catch (err) {
      // ITEM 3 — A DELIBERATE CANCEL IS NOT A FAILURE.
      // A tap during `connecting` calls end(), which closes the wrapper; that rejects
      // the in-flight start waiter, and this catch then set `error` unconditionally —
      // so a cancel landed in error and it took THREE taps to get back into a call.
      // end() owns the teardown and the landing at idle; this path stands aside.
      const msg = String((err as Error)?.message ?? err);
      if (!sessionActiveRef.current || msg.startsWith('voice_start_cancelled')) return;
      await cleanup();
      setDaemonError(msg);
      setMeter((prev) => ({ ...prev, active: false, error: msg }));
      setState('error');
      throw err;
    }
  }, [cleanup, onEvent]);

  const start = useCallback(
    (domain: string, agent: string) => {
      // A call THIS window starts is never adopted, whatever a stale flag says.
      setAdopted(null);
      return open(domain, agent, true);
    }, [open]);

  /** The window opened. Ask the box whether a call is live and, if so, attach to it —
   *  taking the media onto THIS device's mic and speakers. Never creates a call. */
  const attachIfLive = useCallback(
    () => open(activeAgent.domain, activeAgent.agent, false), [open, activeAgent]);

  /**
   * A TAP MUST ALWAYS DO SOMETHING KNOWABLE (Item 3).
   *
   * This used to be `socketRef.current?.endSession()` — and during a failed
   * negotiation socketRef was still null, so the optional chain made the whole tap a
   * silent no-op: no /voice-b event, no audit, NO SERVER TRACE. That is why three of
   * four attempts vanished; the owner tapped, believed he was talking to her, and
   * nothing left his phone. Every branch below now ends in a visible outcome.
   */
  /**
   * END THE CALL — the ONLY teardown, from every state (Seam 1).
   *
   * The sphere is a call button with two states. There is no separate cancel: ending
   * while `connecting` IS the cancel, and it runs the same teardown — the wrapper is
   * closed, which releases the microphone and rejects anything parked on the start
   * path. Every branch leaves a visible outcome; none of them is silence.
   */
  const end = useCallback(() => {
    const wasConnecting = stateRef.current === 'connecting';
    sessionActiveRef.current = false;
    const socket = socketRef.current;
    const delivered = socket ? socket.endSession() : false;

    // ── THE DEVICE GOES NOW. THE CHANNEL STAYS UNTIL THE BOX AGREES ──────────
    // This used to run the full cleanup() immediately, which CLOSED THE SOCKET —
    // the only channel that could deliver the end. On 2026-08-05 the socket was
    // already dropping when the owner tapped, endSession() returned false without
    // emitting, cleanup() then destroyed the socket, and the audit carries no
    // USER_END for that tap at all. The call stayed live and the banner pointed at
    // a button wired to nothing.
    //
    // So the two are separated. The microphone and peer are released immediately —
    // the mic never outlives the tap — and the socket is kept so the emit (buffered
    // by socket.io if it had to reconnect) can leave and the box's answer can
    // arrive. call_state:null or session_ended then runs the real cleanup.
    if (socket) {
      // ── IS THERE ANYTHING LEFT TO END? ────────────────────────────────────
      // `ending` means "the box has been told and has not answered yet". It is
      // only honest while a call exists to be ended. When neither the box nor
      // this wrapper holds one, waiting on an answer that is not coming WEDGES
      // THE SURFACE: `ending` is not `idle`, so the sphere goes on meaning END,
      // and the owner cannot end the call — because there is none — and cannot
      // start one either. That is strictly worse than the fault it replaced, and
      // it is what the owner hit on 2026-08-05.
      //
      // Nothing here is a timeout, a reaper or a grace period, and no timer is
      // introduced: the end has ALREADY been emitted above, unconditionally. This
      // decides only where the SURFACE lands afterwards, and it lands on the
      // truth. A cancel during `connecting` takes this path too — cleanup() is
      // the cancel, closing the wrapper and rejecting the parked start.
      const nothingToEnd = boxCallRef.current == null && socket.getCall() == null;
      if (nothingToEnd) {
        // The DEVICE goes — the mic never outlives the tap — but the SOCKET is
        // kept as glass, deliberately. cleanup() would close it, and a window
        // that has just cancelled is exactly the window that needs to still hear
        // `call_state` if the box turns out to hold a call after all (a
        // session_start that landed behind the cancel). Deaf is the one thing it
        // must not be. The wrapper is already `closing`, so it listens and does
        // not resume; the next tap replaces it.
        socket.releaseMedia();
        try { stopStreamMeter(); } catch {}
        zeroMic();
        setAdopted(null);
        setState('idle');
        setDaemonError(wasConnecting
          ? 'CANCELLED: the box never confirmed a call, so there was none to end'
          : null);
        return;
      }
      socket.releaseMedia();
      // The device is gone, so the device's readouts go with it. A meter still
      // moving on a call being ended is a small lie of exactly the kind this
      // surface has been paying for all day.
      try { stopStreamMeter(); } catch {}
      zeroMic();
      setState('ending');
      if (!delivered) {
        setDaemonError(wasConnecting
          ? 'CANCELLING: your end is buffered — reconnecting to deliver it'
          : 'ENDING: your end is buffered — reconnecting to deliver it');
      } else {
        setDaemonError('ENDING: your end reached the box — waiting for its answer');
      }
      return;
    }

    // NO SOCKET AT ALL. Nothing can be delivered from here, and saying the call
    // ended would be the lie this whole rail exists to prevent.
    void cleanup().then(() => {
      setState('idle');
      setDaemonError('NO CALL TO END: this window holds no connection to the box');
    });
  }, [cleanup, zeroMic]);

  const setMuted = useCallback((m: boolean) => {
    if (m) socketRef.current?.mute();
    else socketRef.current?.unmute();
  }, []);

  /**
   * OPENING A WINDOW NEVER ENDS A CALL — and a window that opens into a live call
   * shows it, rather than showing idle while the box is mid-conversation.
   *
   * Runs once per mounted hook. Guarded by a ref because React StrictMode double-
   * invokes effects in development, and asking twice would open two sockets. If there
   * is no call this is a no-op that leaves the box exactly as it found it.
   */
  const attachProbedRef = useRef(false);
  useEffect(() => {
    if (attachProbedRef.current) return;
    attachProbedRef.current = true;
    void attachIfLive().catch(() => { /* no call, or the box is unreachable */ });
  }, [attachIfLive]);

  /**
   * THE RETURN (Fix 3, owner-ruled 2026-08-10).
   *
   * There was no app-foreground listener at all: recovery was opportunistic,
   * riding socket reconnect and native media events. This makes the return
   * DELIBERATE, and it calls the existing `resumeTransport` path — it does not
   * invent a second one.
   *
   * `visibilitychange`, not `@capacitor/app`. That plugin is NOT a dependency of
   * this project (only core, ios, android, cli), and adding a native dependency
   * to the build that finally has to work is a risk with no upside here. This
   * event costs nothing and, being web-side, reaches the phone ON A RELOAD
   * rather than a rebuild. If the instrumentation shows it never fires on
   * device, the plugin becomes justified on evidence rather than on a guess.
   *
   * ── GATED TO THE NATIVE SHELL, AND THAT IS A RAIL, NOT A TIDY-UP ─────────
   * `visibilitychange` is a WEB event: on the desktop it fires on every tab
   * switch. Ungated, every tab-return would run a session_start attach with its
   * audit rows and a fresh keep_media decision — DESKTOP CHANGED IN BEHAVIOUR
   * while every constant stayed identical, which is precisely what the standing
   * check exists to catch. `isNativeShell()` is false in any browser, so the
   * desktop never reaches the listener at all.
   *
   * Only the FOREGROUND edge matters. Whether `hidden` fires on backgrounding is
   * irrelevant to recovery, and the handler runs when JS is live again by
   * definition.
   */
  useEffect(() => {
    if (!isNativeShell()) return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      socketRef.current?.resumeFromForeground('visibilitychange');
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  useEffect(() => () => { void cleanup(); }, [cleanup]);

  return {
    state,
    boxCall,
    /** Non-null when the live call was ALREADY RUNNING when this window reached
     *  it — with the box's own start time, so the surface can say for how long
     *  rather than implying this window started it. */
    adopted,
    mediaConnected,
    agentText,
    partialTranscript,
    muted,
    /** Seconds this mute has stood, as the BOX measures it — 0 on the edge, then
     *  restated on every lane heartbeat for as long as the microphone is shut.
     *  A surface must never compute this itself: the box owns the mute. */
    mutedStandingS,
    activeAgent,
    userAmplitude,
    meter,
    error: daemonError,
    start,
    end,
    setMuted,
  };
}
